// Text anchoring: locating a saved quote in an article, robustly.
//
// The naive approach — store the quote, then `indexOf` it on render — is what
// Yera did, and it fails in two ways that matter. A phrase appearing twice
// anchors to whichever came first, silently pointing at the wrong sentence;
// and when the article changes even slightly, the match fails and the
// highlight is dropped (Yera's fallback highlighted the whole enclosing
// paragraph instead, which is worse than nothing).
//
// So an annotation stores three things, following the W3C Web Annotation
// model: the `quote`, a short `prefix`/`suffix` of surrounding text, and a
// `position_hint` character offset. Resolution tries them in order of
// confidence and reports how sure it is, so the UI can show an unresolvable
// annotation as orphaned rather than attaching it somewhere wrong.

import type { Annotation } from './types.ts';

/** How an annotation's quote was located, in descending confidence. */
export type LocateMethod = 'exact' | 'context' | 'fuzzy' | 'orphan';

export type Located =
  | { method: Exclude<LocateMethod, 'orphan'>; start: number; end: number; confidence: number }
  | { method: 'orphan'; confidence?: number; start?: undefined; end?: undefined };

/** The anchor fields as *stored*, where context may be absent. */
export type Anchor = Pick<Annotation, 'quote'> &
  Partial<Pick<Annotation, 'prefix' | 'suffix' | 'position_hint'>>;

/**
 * The anchor as *captured* from a live selection. Distinct from `Anchor`
 * because describing a selection always yields context — possibly empty
 * strings at the edges of the article, but never null — and the create
 * endpoint accepts it directly.
 */
export interface SelectionAnchor {
  quote: string;
  prefix: string;
  suffix: string;
  position_hint: number;
}

export const CONTEXT_LENGTH = 32;

// Below this, a fuzzy match is more likely to be a coincidence than the
// original passage, so we orphan the annotation instead of guessing.
export const FUZZY_THRESHOLD = 0.75;

// How far either side of the position hint to search for a fuzzy match.
// Unbounded search over a long article is both slow and more likely to find a
// false positive somewhere unrelated.
const FUZZY_WINDOW = 3000;

/**
 * Collapse whitespace so that HTML's arbitrary indentation and line wrapping
 * don't affect matching. Text extracted from the DOM and text saved at
 * selection time must go through this identically.
 */
export function normalizeText(value: unknown): string {
  return String(value == null ? '' : value).replace(/\s+/g, ' ');
}

/** Every index in `haystack` where `needle` occurs. */
export function findAll(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const hits: number[] = [];
  let from = 0;
  for (;;) {
    const index = haystack.indexOf(needle, from);
    if (index === -1) return hits;
    hits.push(index);
    from = index + 1; // +1, not +needle.length: overlapping occurrences count
  }
}

/**
 * How much of the expected context actually surrounds a candidate, 0–1.
 *
 * Compared from the inside out — the characters nearest the quote are the ones
 * least likely to have changed, and matter most for telling two occurrences of
 * the same phrase apart.
 */
export function contextScore(
  text: string,
  index: number,
  quote: string,
  prefix: string,
  suffix: string,
): number {
  let matched = 0;
  let expected = 0;

  if (prefix) {
    const before = text.slice(Math.max(0, index - prefix.length), index);
    expected += prefix.length;
    // Walk backwards from the quote, counting characters that agree.
    for (let i = 1; i <= prefix.length; i++) {
      if (before[before.length - i] !== prefix[prefix.length - i]) break;
      matched++;
    }
  }

  if (suffix) {
    const after = text.slice(index + quote.length, index + quote.length + suffix.length);
    expected += suffix.length;
    for (let i = 0; i < suffix.length; i++) {
      if (after[i] !== suffix[i]) break;
      matched++;
    }
  }

  if (expected === 0) return 1; // nothing to disagree with
  return matched / expected;
}

/** Sørensen–Dice over character bigrams: cheap, and forgiving of small edits. */
export function similarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;

  const bigrams = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const gram = a.slice(i, i + 2);
    bigrams.set(gram, (bigrams.get(gram) || 0) + 1);
  }

  let shared = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const gram = b.slice(i, i + 2);
    const count = bigrams.get(gram) || 0;
    if (count > 0) {
      bigrams.set(gram, count - 1);
      shared++;
    }
  }

  return (2 * shared) / (a.length + b.length - 2);
}

/**
 * Best approximate match for `quote`, searched in a window around `hint`.
 * Returns `{index, score}` or null.
 */
export function fuzzyFind(
  text: string,
  quote: string,
  hint = 0,
): { index: number; score: number } | null {
  if (!quote || quote.length < 4 || !text) return null;

  const start = Math.max(0, (hint || 0) - FUZZY_WINDOW);
  const end = Math.min(text.length, (hint || 0) + FUZZY_WINDOW + quote.length);
  const region = text.slice(start, end);
  if (region.length < quote.length) return null;

  // Step in proportion to the quote so long passages don't cost more to find;
  // a coarse pass locates the neighbourhood, then a fine pass refines it.
  const coarseStep = Math.max(1, Math.floor(quote.length / 4));
  let best = { index: -1, score: 0 };

  for (let i = 0; i + quote.length <= region.length; i += coarseStep) {
    const score = similarity(quote, region.slice(i, i + quote.length));
    if (score > best.score) best = { index: i, score };
  }

  if (best.index === -1) return null;

  const fineFrom = Math.max(0, best.index - coarseStep);
  const fineTo = Math.min(region.length - quote.length, best.index + coarseStep);
  for (let i = fineFrom; i <= fineTo; i++) {
    const score = similarity(quote, region.slice(i, i + quote.length));
    if (score > best.score) best = { index: i, score };
  }

  return best.score > 0 ? { index: start + best.index, score: best.score } : null;
}

/**
 * Locate an annotation's quote in the article text.
 *
 * @returns {{start: number, end: number, confidence: number, method: string}}
 *          or `{method: 'orphan'}` when nothing is trustworthy enough.
 */
export function locate(text: string, annotation: Anchor): Located {
  const quote = normalizeText(annotation.quote);
  if (!quote || !text) return { method: 'orphan' };

  const prefix = normalizeText(annotation.prefix || '');
  const suffix = normalizeText(annotation.suffix || '');
  const hint = typeof annotation.position_hint === 'number' && Number.isFinite(annotation.position_hint)
    ? annotation.position_hint
    : 0;

  const exact = findAll(text, quote);

  if (exact.length === 1) {
    const start = exact[0]!;
    return { start, end: start + quote.length, confidence: 1, method: 'exact' };
  }

  if (exact.length > 1) {
    // Ambiguous. Context decides; position breaks a tie between candidates
    // whose context is equally good (or equally absent).
    let best: { index: number; score: number; distance: number } | null = null;
    for (const index of exact) {
      const score = contextScore(text, index, quote, prefix, suffix);
      const distance = Math.abs(index - hint);
      if (!best || score > best.score || (score === best.score && distance < best.distance)) {
        best = { index, score, distance };
      }
    }
    return {
      start: best!.index,
      end: best!.index + quote.length,
      confidence: best!.score,
      method: 'context',
    };
  }

  const fuzzy = fuzzyFind(text, quote, hint);
  if (fuzzy && fuzzy.score >= FUZZY_THRESHOLD) {
    return {
      start: fuzzy.index,
      end: fuzzy.index + quote.length,
      confidence: fuzzy.score,
      method: 'fuzzy',
    };
  }

  return { method: 'orphan', confidence: fuzzy ? fuzzy.score : 0 };
}

/**
 * Build the anchor fields to store for a new selection.
 * `text` is the whole normalized article, `start`/`end` the selection offsets.
 */
export function describeSelection(
  text: string,
  start: number,
  end: number,
): SelectionAnchor {
  return {
    quote: text.slice(start, end),
    prefix: text.slice(Math.max(0, start - CONTEXT_LENGTH), start),
    suffix: text.slice(end, Math.min(text.length, end + CONTEXT_LENGTH)),
    position_hint: start,
  };
}
